import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas/dist/html2canvas.esm.js';
import autoTable from 'jspdf-autotable';

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {
  
  constructor() { }

  async generateReportPDF(reportData: any, workflowVersion: any, selectedLang: string = 'en', remoteCalls?: any) {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      floatPrecision: 16
    });

    // Load and add Open Sans font for Turkish character support
    await this.loadOpenSansFont(pdf);
    
    // Set default font to Open Sans
    pdf.setFont('OpenSans', 'normal');

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPosition = margin;

    // Add logo with proper aspect ratio
    try {
      const logoImg = await this.loadImage('assets/img/logo.png');
      if (logoImg) {
        // Calculate proper dimensions maintaining aspect ratio
        const maxWidth = 50;
        const maxHeight = 20;
        const imgRatio = logoImg.width / logoImg.height;
        
        let logoWidth = maxWidth;
        let logoHeight = maxWidth / imgRatio;
        
        // If height is too large, scale by height instead
        if (logoHeight > maxHeight) {
          logoHeight = maxHeight;
          logoWidth = maxHeight * imgRatio;
        }
        
        pdf.addImage(logoImg, 'PNG', margin, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 10;
      }
    } catch (error) {
      // Logo not available, continue without it
      yPosition += 10;
    }

    // Title
    pdf.setFontSize(20);
    pdf.setFont('OpenSans', 'bold');
    pdf.text('Workflow Report', margin, yPosition);
    yPosition += 15;

    // Report metadata
    pdf.setFontSize(12);
    pdf.setFont('OpenSans', 'normal');
    
    const metadata = this.getReportMetadata(reportData, workflowVersion, selectedLang);
    
    // Draw metadata table
    autoTable(pdf, {
      startY: yPosition,
      head: [],
      body: metadata,
      theme: 'plain',
      styles: {
        font: 'OpenSans',
        fontStyle: 'normal'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 50 },
        1: { cellWidth: 'auto' }
      },
      margin: { left: margin, right: margin },
      didDrawPage: (data) => {
        yPosition = data.cursor.y + 10;
      }
    });

    // Add page break if needed
    if (yPosition > pageHeight - 50) {
      pdf.addPage();
      yPosition = margin;
    }

    // Section divider
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Add files section if exists
    if (workflowVersion?.files && workflowVersion.files.length > 0) {
      pdf.setFontSize(14);
      pdf.setFont('OpenSans', 'bold');
      pdf.text('Attached Files', margin, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(11);
      pdf.setFont('OpenSans', 'normal');
      pdf.setTextColor(0, 0, 255);
      
      for (const file of workflowVersion.files) {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.textWithLink(file.name, margin + 5, yPosition, { url: file.url });
        yPosition += 6;
      }
      
      pdf.setTextColor(0, 0, 0);
      yPosition += 10;
    }

    // Add extra fields if exists
    if (workflowVersion?.detailExtraFields && workflowVersion.detailExtraFields.length > 0) {
      pdf.setFontSize(14);
      pdf.setFont('OpenSans', 'bold');
      pdf.text('Additional Information', margin, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(11);
      pdf.setFont('OpenSans', 'normal');
      
      for (const field of workflowVersion.detailExtraFields) {
        if (yPosition > pageHeight - 20) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.setFont('OpenSans', 'bold');
        const fieldLabel = `${field.templateOptions?.label || 'Field'}:`;
        const fieldLabelLines = pdf.splitTextToSize(fieldLabel, 50);
        pdf.text(fieldLabelLines, margin + 5, yPosition);
        pdf.setFont('OpenSans', 'normal');
        const fieldValue = String(field.templateOptions?.value || '');
        const fieldValueLines = pdf.splitTextToSize(fieldValue, pageWidth - margin - 65);
        pdf.text(fieldValueLines, margin + 60, yPosition);
        yPosition += Math.max(fieldLabelLines.length, fieldValueLines.length) * 6;
      }
      
      yPosition += 10;
    }

    // Steps section
    pdf.setFontSize(16);
    pdf.setFont('OpenSans', 'bold');
    pdf.text('Workflow Steps', margin, yPosition);
    yPosition += 10;

    // Process each step
    for (const result of reportData.results || []) {
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Step name with duration
      pdf.setFontSize(14);
      pdf.setFont('OpenSans', 'bold');
      const stepName = this.getTranslatedValue(workflowVersion, result.stepName, selectedLang);
      const stepText = result.duration ? `${stepName} (Duration: ${result.duration})` : stepName;
      const stepLines = pdf.splitTextToSize(stepText, pageWidth - margin * 2);
      pdf.text(stepLines, margin, yPosition);
      yPosition += stepLines.length * 7 + 3;

      // Step content
      pdf.setFontSize(11);
      pdf.setFont('OpenSans', 'normal');

      for (const item of result.reportArray || []) {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = margin;
        }

        // Field label
        if (item.data?.label) {
          pdf.setFont('OpenSans', 'bold');
          const label = this.getTranslatedValue(workflowVersion, item.data.label, selectedLang);
          const labelLines = pdf.splitTextToSize(label + ':', pageWidth - margin - 5);
          pdf.text(labelLines, margin + 5, yPosition);
          yPosition += labelLines.length * 6;
          pdf.setFont('OpenSans', 'normal');
        }

        // Field value
        const newY = await this.addFieldContent(pdf, item, margin + 10, yPosition, pageWidth, pageHeight, workflowVersion, selectedLang, result);
        yPosition = newY;
      }

      yPosition += 5;
    }

    // Add remote calls section if exists
    if (remoteCalls && Object.keys(remoteCalls).length > 0) {
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(16);
      pdf.setFont('OpenSans', 'bold');
      pdf.text('Remote Call Information', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(11);
      pdf.setFont('OpenSans', 'normal');

      for (const [key, call] of Object.entries(remoteCalls)) {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = margin;
        }

        if (call['stepName']) {
          const stepName = this.getTranslatedValue(workflowVersion, call['stepName'], selectedLang);
          const stepLines = pdf.splitTextToSize(`Step: ${stepName}`, pageWidth - margin - 5);
          pdf.text(stepLines, margin + 5, yPosition);
          yPosition += stepLines.length * 6;
        }

        if (call['session']?.['joinedUsers']) {
          const users = call['session']['joinedUsers'].map(u => u.name).join(', ');
          const userLines = pdf.splitTextToSize(`Users: ${users}`, pageWidth - margin - 5);
          pdf.text(userLines, margin + 5, yPosition);
          yPosition += userLines.length * 6;
        }

        if (call['session']?.['create_time']) {
          pdf.text(`Created: ${new Date(call['session']['create_time']).toLocaleString()}`, margin + 5, yPosition);
          yPosition += 6;
        }

        if (call['session']?.['start_time']) {
          pdf.text(`Started: ${new Date(call['session']['start_time']).toLocaleString()}`, margin + 5, yPosition);
          yPosition += 6;
        }

        if (call['session']?.['end_time']) {
          pdf.text(`Ended: ${new Date(call['session']['end_time']).toLocaleString()}`, margin + 5, yPosition);
          yPosition += 6;
        }

        yPosition += 5;
      }

      yPosition += 10;
    }

    // Add signature section if exists
    if (reportData.signature?.signers) {
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      pdf.setFontSize(16);
      pdf.setFont('OpenSans', 'bold');
      pdf.text('Approvals', margin, yPosition);
      yPosition += 10;

      for (const signer of reportData.signature.signers) {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = margin;
        }

        pdf.setFontSize(12);
        pdf.setFont('OpenSans', 'normal');
        const signerText = `${signer.name || ''} (${signer.email})`;
        const signerLines = pdf.splitTextToSize(signerText, pageWidth - margin * 2);
        pdf.text(signerLines, margin, yPosition);
        yPosition += signerLines.length * 6;
        
        if (signer.signed && signer.signedAt) {
          pdf.text(`Signed at: ${new Date(signer.signedAt).toLocaleString()}`, margin, yPosition);
          yPosition += 6;
          
          if (signer.signatureUrl) {
            try {
              const sigImg = await this.loadImage(signer.signatureUrl);
              if (sigImg) {
                pdf.addImage(sigImg, 'PNG', margin, yPosition, 40, 20);
                yPosition += 25;
              }
            } catch (error) {
              pdf.text('[Signature]', margin, yPosition);
              yPosition += 8;
            }
          }
        } else {
          pdf.text('Not signed', margin, yPosition);
          yPosition += 8;
        }
      }
    }

    // Add footer to all pages
    const totalPages = (pdf as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setFont('OpenSans', 'normal');
      pdf.text(
        `Page ${i} of ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      pdf.text(
        `Generated on ${new Date().toLocaleDateString()}`,
        pageWidth - margin,
        pageHeight - 10,
        { align: 'right' }
      );
    }

    // Generate filename
    const workflowName = this.getTranslatedValue(workflowVersion, workflowVersion.name, selectedLang) || 'workflow';
    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedName = this.sanitizeFilename(workflowName);
    const filename = `${sanitizedName}_report_${timestamp}.pdf`;

    // Download the PDF
    pdf.save(filename);
  }

  async generateTicketPdfBlob(options: {
    ticket: {
      title?: string;
      status?: string;
      priority?: string;
      type?: string;
      roomName?: string;
      createdBy?: string;
      createdOn?: string;
      assignee?: string;
      followup?: string;
      description?: string;
      resolution?: string;
    };
    sessions?: Array<{ date?: string; roomName?: string; participants?: string; duration?: string }>;
    files?: Array<{ name?: string; url?: string; extension?: string; size?: string; isImage?: boolean; uploader_name?: string; uploader?: string }>;
    chat?: Array<{ timestamp?: string; senderName?: string; text?: string }>;
    includeImagesInPdf?: boolean;
    labels: {
      title: string;
      detailsTitle: string;
      sessionsTitle: string;
      filesTitle: string;
      chatTitle: string;
      labelTitle: string;
      labelStatus: string;
      labelPriority: string;
      labelType: string;
      labelRoom: string;
      labelCreatedBy: string;
      labelCreatedOn: string;
      labelAssignee: string;
      labelFollowup: string;
      descriptionLabel: string;
      resolutionLabel: string;
      noItems: string;
      tableDate: string;
      tableRoom: string;
      tableParticipants: string;
      tableDuration: string;
      tableFileName: string;
      tableFileType: string;
      tableFileSize: string;
      tableFileUploader: string;
      tableChatTime: string;
      tableChatUser: string;
      tableChatMessage: string;
    };
  }): Promise<Blob> {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      floatPrecision: 16
    });

    await this.loadOpenSansFont(pdf);
    pdf.setFont('OpenSans', 'normal');

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let yPosition = margin;

    try {
      const logoImg = await this.loadImage('assets/img/logo.png');
      if (logoImg) {
        const maxWidth = 50;
        const maxHeight = 20;
        const imgRatio = logoImg.width / logoImg.height;
        let logoWidth = maxWidth;
        let logoHeight = maxWidth / imgRatio;
        if (logoHeight > maxHeight) {
          logoHeight = maxHeight;
          logoWidth = maxHeight * imgRatio;
        }
        pdf.addImage(logoImg, 'PNG', margin, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 10;
      }
    } catch (error) {
      yPosition += 10;
    }

    pdf.setFontSize(20);
    pdf.setFont('OpenSans', 'bold');
    pdf.text(this.sanitizeTextForPdf(options.labels.title), margin, yPosition);
    yPosition += 12;

    const detailsRows: string[][] = [];
    const pushRow = (label: string, value?: string) => {
      if (value) {
        detailsRows.push([this.sanitizeTextForPdf(label), this.sanitizeTextForPdf(String(value))]);
      }
    };

    pushRow(options.labels.labelTitle, options.ticket.title);
    pushRow(options.labels.labelStatus, options.ticket.status);
    pushRow(options.labels.labelPriority, options.ticket.priority);
    pushRow(options.labels.labelType, options.ticket.type);
    pushRow(options.labels.labelRoom, options.ticket.roomName);
    pushRow(options.labels.labelCreatedBy, options.ticket.createdBy);
    pushRow(options.labels.labelCreatedOn, options.ticket.createdOn);
    pushRow(options.labels.labelAssignee, options.ticket.assignee);
    pushRow(options.labels.labelFollowup, options.ticket.followup);

    if (detailsRows.length > 0) {
      pdf.setFontSize(14);
      pdf.setFont('OpenSans', 'bold');
      pdf.text(this.sanitizeTextForPdf(options.labels.detailsTitle), margin, yPosition);
      yPosition += 6;

      autoTable(pdf, {
        startY: yPosition,
        head: [],
        body: detailsRows,
        theme: 'plain',
        styles: { font: 'OpenSans', fontStyle: 'normal' },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50 },
          1: { cellWidth: 'auto' }
        },
        margin: { left: margin, right: margin },
        didDrawPage: (data) => {
          yPosition = data.cursor.y + 6;
        }
      });
    }

    const addTextSection = (title: string, content?: string) => {
      if (!content) return;
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.setFontSize(13);
      pdf.setFont('OpenSans', 'bold');
      pdf.text(this.sanitizeTextForPdf(title), margin, yPosition);
      yPosition += 6;
      pdf.setFontSize(11);
      pdf.setFont('OpenSans', 'normal');
      yPosition = this.renderTextWithWrap(pdf, this.sanitizeTextForPdf(content), margin, yPosition, pageWidth, pageHeight, margin) + 4;
    };

    addTextSection(options.labels.descriptionLabel, options.ticket.description);
    addTextSection(options.labels.resolutionLabel, options.ticket.resolution);

    if (options.sessions) {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.setFontSize(13);
      pdf.setFont('OpenSans', 'bold');
      pdf.text(this.sanitizeTextForPdf(options.labels.sessionsTitle), margin, yPosition);
      yPosition += 6;

      const rows = options.sessions.map(session => ([
        this.sanitizeTextForPdf(session.date || ''),
        this.sanitizeTextForPdf(session.roomName || ''),
        this.sanitizeTextForPdf(session.participants || ''),
        this.sanitizeTextForPdf(session.duration || '')
      ]));

      if (rows.length === 0) {
        pdf.setFontSize(11);
        pdf.setFont('OpenSans', 'normal');
        pdf.text(this.sanitizeTextForPdf(options.labels.noItems), margin, yPosition);
        yPosition += 6;
      } else {
        autoTable(pdf, {
          startY: yPosition,
          head: [[
            this.sanitizeTextForPdf(options.labels.tableDate),
            this.sanitizeTextForPdf(options.labels.tableRoom),
            this.sanitizeTextForPdf(options.labels.tableParticipants),
            this.sanitizeTextForPdf(options.labels.tableDuration)
          ]],
          body: rows,
          theme: 'grid',
          styles: { font: 'OpenSans', fontStyle: 'normal', fontSize: 9 },
          headStyles: { fontStyle: 'bold' },
          margin: { left: margin, right: margin },
          didDrawPage: (data) => {
            yPosition = data.cursor.y + 6;
          }
        });
      }
    }

    if (options.files) {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.setFontSize(13);
      pdf.setFont('OpenSans', 'bold');
      pdf.text(this.sanitizeTextForPdf(options.labels.filesTitle), margin, yPosition);
      yPosition += 6;

      const rows = options.files.map(file => ([
        this.sanitizeTextForPdf(file.name || ''),
        this.sanitizeTextForPdf(file.extension || ''),
        this.sanitizeTextForPdf(file.size || ''),
        this.sanitizeTextForPdf(file.uploader_name || file.uploader || '')
      ]));

      if (rows.length === 0) {
        pdf.setFontSize(11);
        pdf.setFont('OpenSans', 'normal');
        pdf.text(this.sanitizeTextForPdf(options.labels.noItems), margin, yPosition);
        yPosition += 6;
      } else {
        autoTable(pdf, {
          startY: yPosition,
          head: [[
            this.sanitizeTextForPdf(options.labels.tableFileName),
            this.sanitizeTextForPdf(options.labels.tableFileType),
            this.sanitizeTextForPdf(options.labels.tableFileSize),
            this.sanitizeTextForPdf(options.labels.tableFileUploader)
          ]],
          body: rows,
          theme: 'grid',
          styles: { font: 'OpenSans', fontStyle: 'normal', fontSize: 9 },
          headStyles: { fontStyle: 'bold' },
          margin: { left: margin, right: margin },
          didDrawPage: (data) => {
            yPosition = data.cursor.y + 6;
          }
        });
      }

      if (options.includeImagesInPdf) {
        for (const file of options.files) {
          if (!file?.url || !file?.isImage) {
            continue;
          }
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.setFontSize(11);
          pdf.setFont('OpenSans', 'bold');
          pdf.text(this.sanitizeTextForPdf(file.name || ''), margin, yPosition);
          yPosition += 6;

          const { usedHeight } = await this.addImageToPDF(pdf, file.url, margin, yPosition, pageWidth, pageHeight, margin);
          yPosition += (usedHeight || 0) + 6;
        }
      }
    }

    if (options.chat) {
      if (yPosition > pageHeight - 30) {
        pdf.addPage();
        yPosition = margin;
      }
      pdf.setFontSize(13);
      pdf.setFont('OpenSans', 'bold');
      pdf.text(this.sanitizeTextForPdf(options.labels.chatTitle), margin, yPosition);
      yPosition += 6;

      const rows = options.chat.map(item => ([
        this.sanitizeTextForPdf(item.timestamp || ''),
        this.sanitizeTextForPdf(item.senderName || ''),
        this.sanitizeTextForPdf(item.text || '')
      ]));

      if (rows.length === 0) {
        pdf.setFontSize(11);
        pdf.setFont('OpenSans', 'normal');
        pdf.text(this.sanitizeTextForPdf(options.labels.noItems), margin, yPosition);
        yPosition += 6;
      } else {
        autoTable(pdf, {
          startY: yPosition,
          head: [[
            this.sanitizeTextForPdf(options.labels.tableChatTime),
            this.sanitizeTextForPdf(options.labels.tableChatUser),
            this.sanitizeTextForPdf(options.labels.tableChatMessage)
          ]],
          body: rows,
          theme: 'grid',
          styles: { font: 'OpenSans', fontStyle: 'normal', fontSize: 9 },
          headStyles: { fontStyle: 'bold' },
          margin: { left: margin, right: margin },
          didDrawPage: (data) => {
            yPosition = data.cursor.y + 6;
          }
        });
      }
    }

    const totalPages = (pdf as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setFont('OpenSans', 'normal');
      pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      pdf.text(`Generated on ${new Date().toLocaleDateString()}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }

    return pdf.output('blob');
  }

  private renderTextWithWrap(
    pdf: jsPDF,
    text: string,
    x: number,
    y: number,
    pageWidth: number,
    pageHeight: number,
    margin: number
  ): number {
    if (!text) return y;
    
    // Calculate text width with proper margin
    // pageWidth = 210mm, x = 25mm, margin = 15mm
    // We want text to end at pageWidth - margin = 195mm
    // So maxWidth should be: 195 - 25 = 170mm
    const rightEdge = pageWidth - margin;
    const maxWidth = rightEdge - x;
    
    const lines = pdf.splitTextToSize(text, maxWidth);
    let currentY = y;
    
    for (let i = 0; i < lines.length; i++) {
      if (currentY > pageHeight - 30) {
        pdf.addPage();
        currentY = margin;
      }
      // Render text line by line - jsPDF will use courier font with better encoding
      pdf.text(lines[i], x, currentY);
      currentY += 5;
    }
    
    return currentY;
  }

  private async loadOpenSansFont(pdf: jsPDF): Promise<void> {
    try {
      // Load Open Sans Regular font
      const regularFontResponse = await fetch('assets/fonts/Open-Sans/OpenSans-Regular.ttf');
      const regularFontBlob = await regularFontResponse.arrayBuffer();
      const regularFontBase64 = this.arrayBufferToBase64(regularFontBlob);
      
      // Load Open Sans Bold font
      const boldFontResponse = await fetch('assets/fonts/Open-Sans/OpenSans-Bold.ttf');
      const boldFontBlob = await boldFontResponse.arrayBuffer();
      const boldFontBase64 = this.arrayBufferToBase64(boldFontBlob);
      
      // Add fonts to jsPDF
      pdf.addFileToVFS('OpenSans-Regular.ttf', regularFontBase64);
      pdf.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
      
      pdf.addFileToVFS('OpenSans-Bold.ttf', boldFontBase64);
      pdf.addFont('OpenSans-Bold.ttf', 'OpenSans', 'bold');
    } catch (error) {
      console.error('Failed to load Open Sans fonts:', error);
      // Fallback to courier if font loading fails
      pdf.setFont('OpenSans', 'normal');
    }
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }


  private async addFieldContent(
    pdf: jsPDF, 
    item: any, 
    x: number, 
    y: number, 
    pageWidth: number,
    pageHeight: number,
    workflowVersion: any,
    selectedLang: string,
    result?: any
  ): Promise<number> {
    const margin = 15;
    let currentY = y;

    if (!item.data?.value && item.data?.type) {
      pdf.setTextColor(161, 161, 161);
      pdf.text('No content', x, currentY);
      pdf.setTextColor(0, 0, 0);
      return currentY + 6;
    }

    // Handle different field types
    switch (item.data.type) {
      case 'image':
      case 'captureImage':
      case 'signature':
        // Handle regular image widgets
        if (Array.isArray(item.data.value)) {
          for (const img of item.data.value) {
            const { usedHeight, pageBreak } = await this.addImageToPDF(
              pdf,
              img.url || img,
              x,
              currentY,
              pageWidth,
              pageHeight,
              margin
            );
            currentY = pageBreak ? margin + usedHeight + 5 : currentY + usedHeight + 5;
          }
        } else if (item.data.value) {
          const { usedHeight, pageBreak } = await this.addImageToPDF(
            pdf,
            item.data.value,
            x,
            currentY,
            pageWidth,
            pageHeight,
            margin
          );
          currentY = pageBreak ? margin + usedHeight + 5 : currentY + usedHeight + 5;
        }
        break;
        
      case 'ppe':
        // PPE specific handling - show detection status
        if (item.data.value?.detected !== undefined) {
          const detectionStatus = `Detection Status: ${item.data.value.detected ? 'Detected' : 'Not Detected'}`;
          currentY = this.renderTextWithWrap(pdf, detectionStatus, x, currentY, pageWidth, pageHeight, margin);
        }
        
        // Show image if exists and detected
        if (item.data.value?.link && item.data.value.detected) {
          const { usedHeight, pageBreak } = await this.addImageToPDF(
            pdf,
            item.data.value.link,
            x,
            currentY,
            pageWidth,
            pageHeight,
            margin
          );
          currentY = pageBreak ? margin + usedHeight + 5 : currentY + usedHeight + 5;
        } else if (!item.data.value?.detected) {
          pdf.setTextColor(255, 0, 0);
          pdf.text('Not Detected', x, currentY);
          pdf.setTextColor(0, 0, 0);
          currentY += 6;
        }
        break;
        
      case 'gallery':
        if (item.data.value && Array.isArray(item.data.value)) {
          for (const galleryItem of item.data.value) {
            if (galleryItem.imgUrl) {
              const { usedHeight, pageBreak } = await this.addImageToPDF(
                pdf,
                galleryItem.imgUrl,
                x,
                currentY,
                pageWidth,
                pageHeight,
                margin
              );
              currentY = pageBreak ? margin + usedHeight + 5 : currentY + usedHeight + 5;
            }
          }
        }
        break;

      case 'video':
      case 'captureVideo':
        pdf.setTextColor(0, 0, 255);
        pdf.setFont('OpenSans', 'underline');
        if (Array.isArray(item.data.value)) {
          for (const video of item.data.value) {
            const videoUrl = video.url || video;
            const linkText = 'Click here to view video';
            pdf.textWithLink(linkText, x, currentY, { url: videoUrl });
            currentY += 6;
          }
        } else if (item.data.value) {
          const linkText = 'Click here to view video';
          pdf.textWithLink(linkText, x, currentY, { url: item.data.value });
          currentY += 6;
        }
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('OpenSans', 'normal');
        break;

      case 'pdf':
        pdf.setTextColor(0, 0, 255);
        pdf.setFont('OpenSans', 'underline');
        const pdfLinkText = 'Click here to view PDF document';
        pdf.textWithLink(pdfLinkText, x, currentY, { url: item.data.value });
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('OpenSans', 'normal');
        currentY += 6;
        break;

      case 'multicheckbox':
        // First show options
        if (item.data.options && item.data.options.length > 0) {
          const optionLabels = item.data.options.map(opt => 
            this.getTranslatedValue(workflowVersion, opt.label, selectedLang)
          ).join(', ');
          currentY = this.renderTextWithWrap(pdf, `Options: ${optionLabels}`, x, currentY, pageWidth, pageHeight, margin);
        }
        
        // Then show selected values
        if (item.data.values && item.data.values.length > 0) {
          const values = item.data.values.map(v => 
            this.getTranslatedValue(workflowVersion, v, selectedLang)
          ).join(', ');
          currentY = this.renderTextWithWrap(pdf, `Selected: ${values}`, x, currentY, pageWidth, pageHeight, margin);
        } else if (item.data.value && Array.isArray(item.data.value) && item.data.value.length > 0) {
          const values = item.data.value.map(v => 
            this.getTranslatedValue(workflowVersion, v, selectedLang)
          ).join(', ');
          currentY = this.renderTextWithWrap(pdf, `Selected: ${values}`, x, currentY, pageWidth, pageHeight, margin);
        } else {
          pdf.setTextColor(161, 161, 161);
          pdf.text('No selections', x, currentY);
          pdf.setTextColor(0, 0, 0);
          currentY += 6;
        }
        break;

      case 'radio':
        // First show options
        if (item.data.options && item.data.options.length > 0) {
          const radioOptionLabels = item.data.options.map(opt => 
            this.getTranslatedValue(workflowVersion, opt.label, selectedLang)
          ).join(', ');
          currentY = this.renderTextWithWrap(pdf, `Options: ${radioOptionLabels}`, x, currentY, pageWidth, pageHeight, margin);
        }
        
        // Then show selected value
        if (item.data.value && Array.isArray(item.data.value) && item.data.value.length > 0) {
          // Get the selected value id from array
          const selectedValueId = item.data.value[0];
          
          // Find the matching option by value and get its label
          let radioSelectedLabel = selectedValueId;
          if (item.data.options && item.data.options.length > 0) {
            const selectedOption = item.data.options.find(opt => opt.value === selectedValueId);
            if (selectedOption && selectedOption.label) {
              radioSelectedLabel = this.getTranslatedValue(workflowVersion, selectedOption.label, selectedLang);
            }
          }
          
          currentY = this.renderTextWithWrap(pdf, `Selected: ${radioSelectedLabel}`, x, currentY, pageWidth, pageHeight, margin);
        } else {
          pdf.setTextColor(161, 161, 161);
          pdf.text('No selection', x, currentY);
          pdf.setTextColor(0, 0, 0);
          currentY += 6;
        }
        break;

      case 'datepicker':
        let dateValue = '';
        if (typeof item.data.value === 'string') {
          dateValue = item.data.value;
        } else if (typeof item.data.value === 'number') {
          dateValue = new Date(item.data.value).toLocaleDateString();
        }
        currentY = this.renderTextWithWrap(pdf, dateValue, x, currentY, pageWidth, pageHeight, margin);
        break;

      case 'qr':
        if (item.data.enteredQRValue) {
          currentY = this.renderTextWithWrap(pdf, `Entered: ${item.data.enteredQRValue}`, x, currentY, pageWidth, pageHeight, margin);
        }
        if (item.data.value) {
          let qrValue = '';
          if (typeof item.data.value === 'object' && item.data.value.qr) {
            qrValue = String(item.data.value.qr);
          } else if (typeof item.data.value === 'object') {
            qrValue = JSON.stringify(item.data.value);
          } else {
            qrValue = String(item.data.value);
          }
          currentY = this.renderTextWithWrap(pdf, `Scanned: ${qrValue}`, x, currentY, pageWidth, pageHeight, margin);
        }
        break;

      case 'table':
        if (item.data.value && workflowVersion?.steps?.[result.stepId]?.data?.layouts?.[0]?.items?.[0]?.templateOptions?.tableData) {
          const tableStructure = workflowVersion.steps[result.stepId].data.layouts[0].items[0].templateOptions.tableData;
          const tableData = await this.prepareWorkflowTableData(item.data.value, tableStructure, workflowVersion, selectedLang);
          
          if (tableData.headers.length > 0) {
            // Calculate available width and distribute equally among columns
            const availableWidth = pageWidth - (margin * 2);
            const columnWidth = availableWidth / tableData.headers.length;
            const dynamicColumnStyles: any = {};
            
            tableData.headers.forEach((_: any, index: number) => {
              dynamicColumnStyles[index] = { cellWidth: columnWidth };
            });
            
            autoTable(pdf, {
              startY: currentY,
              head: [tableData.headers],
              body: tableData.rows,
              theme: 'striped',
              headStyles: { fillColor: [225, 225, 225], textColor: [0, 0, 0], fontStyle: 'bold', font: 'OpenSans' },
              margin: { left: margin, right: margin },
              styles: { fontSize: 9, cellPadding: 3, font: 'OpenSans', overflow: 'linebreak', halign: 'left' },
              columnStyles: dynamicColumnStyles,
              tableWidth: availableWidth,
              didDrawPage: (data) => {
                currentY = data.cursor.y + 10;
              }
            });
          }
        }
        break;
        
      case 'form':
        if (item.data.value && workflowVersion?.steps?.[result.stepId]?.data?.layouts?.[0]?.items?.[0]?.templateOptions?.formData) {
          const formStructure = workflowVersion.steps[result.stepId].data.layouts[0].items[0].templateOptions.formData;
          const formData = this.prepareFormData(item.data.value, formStructure, workflowVersion, selectedLang);
          
          for (const formSection of formData) {
            if (currentY > pageHeight - 40) {
              pdf.addPage();
              currentY = margin;
            }
            
            // Form section title
            if (formSection.title) {
              pdf.setFont('OpenSans', 'bold');
              pdf.text(formSection.title, x, currentY);
              currentY += 8;
              pdf.setFont('OpenSans', 'normal');
            }
            
            // Form fields as table
            if (formSection.fields.length > 0) {
              autoTable(pdf, {
                startY: currentY,
                head: [],
                body: formSection.fields,
                theme: 'plain',
                columnStyles: {
                  0: { fontStyle: 'bold', cellWidth: 60 },
                  1: { cellWidth: 'auto' }
                },
                margin: { left: margin, right: margin },
                styles: { fontSize: 9, cellPadding: 2, font: 'OpenSans', overflow: 'linebreak' },
                didDrawPage: (data) => {
                  currentY = data.cursor.y + 5;
                }
              });
            }
            
            currentY += 10;
          }
        }
        break;

      case 'html':
        let htmlContent = '';
        
        // Get HTML content from html field or value
        if (item.data.html) {
          htmlContent = this.getTranslatedValue(workflowVersion, item.data.html, selectedLang);
        } else if (item.data.value) {
          htmlContent = String(item.data.value);
        }
        
        // If htmlContent is a URL, show as link
        if (htmlContent && (htmlContent.startsWith('http') || htmlContent.startsWith('https'))) {
          try {
            pdf.setTextColor(0, 0, 255);
            pdf.setFont('OpenSans', 'underline');
            pdf.textWithLink('Click here to view content', x, currentY, { url: htmlContent });
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('OpenSans', 'normal');
            currentY += 6;
          } catch (error) {
            pdf.text('Content not available', x, currentY);
            currentY += 6;
          }
        } else {
          // Render HTML as image
          try {
            const renderedY = await this.renderHtmlToPdf(pdf, htmlContent, x, currentY, pageWidth, pageHeight, margin);
            currentY = renderedY;
          } catch (error) {
            console.error('Error processing HTML content:', error);
            pdf.setTextColor(255, 0, 0);
            pdf.text('Content processing error', x, currentY);
            pdf.setTextColor(0, 0, 0);
            currentY += 6;
          }
        }
        break;

      default:
        let textValue = '';
        if (item.data.type === 'number' || item.data.type === 'input' || item.data.type === 'id') {
          textValue = String(item.data.value || '');
        } else {
          textValue = this.getTranslatedValue(workflowVersion, item.data.value, selectedLang);
        }
        
        if (item.data.unit) {
          textValue += ' ' + item.data.unit;
        }
        
        // Ensure textValue is always a string and sanitize it
        textValue = this.sanitizeTextForPdf(String(textValue || ''));
        
        // Use helper function for safe text wrapping
        currentY = this.renderTextWithWrap(pdf, textValue, x, currentY, pageWidth, pageHeight, margin);
        break;
    }

    return currentY;
  }

  private async addImageToPDF(
    pdf: jsPDF,
    imageUrl: string,
    x: number,
    y: number,
    pageWidth: number,
    pageHeight: number,
    margin: number
  ): Promise<{ usedHeight: number; pageBreak: boolean }> {
    try {
      const img = await this.loadImage(imageUrl);
      if (!img) return { usedHeight: 0, pageBreak: false };

      // Target the full available width in the current content area
      const maxWidth = pageWidth - x - margin;
      const imgAspect = img.height / img.width;
      const scaledWidth = maxWidth;
      const scaledHeight = scaledWidth * imgAspect;

      // If image doesn't fit on current page, add a new page first
      const bottomLimit = pageHeight - margin - 10; // leave some breathing room above footer
      let pageBreak = false;
      if (y + scaledHeight > bottomLimit) {
        pdf.addPage();
        y = margin;
        pageBreak = true;
      }

      // Determine format from url when possible, default to JPEG
      let format: 'JPEG' | 'PNG' = 'JPEG';
      const lowerUrl = (imageUrl || '').toLowerCase();
      if (lowerUrl.startsWith('data:image/png') || lowerUrl.endsWith('.png')) {
        format = 'PNG';
      }

      pdf.addImage(img, format, x, y, scaledWidth, scaledHeight);
      return { usedHeight: scaledHeight, pageBreak };
    } catch (error) {
      pdf.setTextColor(255, 0, 0);
      pdf.text('[Image could not be loaded]', x, y);
      pdf.setTextColor(0, 0, 0);
      return { usedHeight: 0, pageBreak: false };
    }
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  private getReportMetadata(reportData: any, workflowVersion: any, selectedLang: string): any[] {
    const metadata = [];
    
    if (workflowVersion?.name) {
      const name = this.getTranslatedValue(workflowVersion, workflowVersion.name, selectedLang);
      metadata.push(['Workflow Name', name]);
    }
    
    if (workflowVersion?.description) {
      const description = this.getTranslatedValue(workflowVersion, workflowVersion.description, selectedLang);
      metadata.push(['Description', description]);
    }
    
    if (reportData?.results) {
      metadata.push(['Steps Completed', String(reportData.results.length)]);
    }
    
    if (reportData?.startTime) {
      metadata.push(['Start Time', new Date(reportData.startTime).toLocaleString()]);
    }
    
    if (reportData?.endTime) {
      metadata.push(['End Time', new Date(reportData.endTime).toLocaleString()]);
    }
    
    if (reportData?.duration) {
      metadata.push(['Duration', reportData.duration]);
    }
    
    return metadata;
  }

  private getTranslatedValue(workflowVersion: any, key: any, selectedLang: string): string {
    if (!key) return '';
    
    // If key is not a string, convert it
    const keyStr = String(key);
    
    // Check if translation exists
    if (workflowVersion?.translations?.[selectedLang]?.[keyStr]) {
      const result = String(workflowVersion.translations[selectedLang][keyStr]);
      return this.sanitizeTextForPdf(result);
    }
    
    // If key looks like a translation ID (starts with $~#-) and no translation found, return empty
    if (keyStr.startsWith('$~#-') && keyStr.includes('$~#')) {
      return '';
    }
    
    // Otherwise return the key itself (for non-translated values)
    return this.sanitizeTextForPdf(keyStr);
  }

  
  private sanitizeTextForPdf(text: string): string {
    if (!text) return '';
    
    // Universal text cleaning - works for all languages
    let sanitized = String(text);
    
    // Only remove control characters that cause PDF rendering issues
    sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
    
    // Normalize line endings
    sanitized = sanitized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    return sanitized;
  }
  
  private extractStyleFromHtml(html: string): { text: string, color: number[], fontSize: number } {
    const defaultColor = [0, 0, 0]; // black
    const defaultFontSize = 11; // default PDF font size
    
    if (!html || (!html.includes('color') && !html.includes('font-size'))) {
      return { text: html, color: defaultColor, fontSize: defaultFontSize };
    }
    
    let rgbColor = defaultColor;
    let fontSize = defaultFontSize;
    
    // Extract color from style attribute
    const colorMatch = html.match(/color:\s*([^;"']+)/i);
    if (colorMatch) {
      const colorValue = colorMatch[1].trim();
      
      // Handle different color formats
      if (colorValue.startsWith('#')) {
        // Hex color
        const hex = colorValue.substring(1);
        if (hex.length === 3) {
          // Short hex
          rgbColor = [
            parseInt(hex[0] + hex[0], 16),
            parseInt(hex[1] + hex[1], 16),
            parseInt(hex[2] + hex[2], 16)
          ];
        } else if (hex.length === 6) {
          // Full hex
          rgbColor = [
            parseInt(hex.substring(0, 2), 16),
            parseInt(hex.substring(2, 4), 16),
            parseInt(hex.substring(4, 6), 16)
          ];
        }
      } else if (colorValue.startsWith('rgb(')) {
        // RGB color
        const rgbMatch = colorValue.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        if (rgbMatch) {
          rgbColor = [
            parseInt(rgbMatch[1]),
            parseInt(rgbMatch[2]),
            parseInt(rgbMatch[3])
          ];
        }
      } else if (colorValue.startsWith('rgba(')) {
        // RGBA color (ignore alpha)
        const rgbaMatch = colorValue.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d\.]+\)/);
        if (rgbaMatch) {
          rgbColor = [
            parseInt(rgbaMatch[1]),
            parseInt(rgbaMatch[2]),
            parseInt(rgbaMatch[3])
          ];
        }
      }
    }
    
    // Extract font size from style attribute
    const fontSizeMatch = html.match(/font-size:\s*([^;"']+)/i);
    if (fontSizeMatch) {
      const fontSizeValue = fontSizeMatch[1].trim();
      
      // Handle different font size formats
      if (fontSizeValue.endsWith('px')) {
        const pxValue = parseInt(fontSizeValue.replace('px', ''));
        fontSize = Math.max(8, Math.min(24, pxValue * 0.75)); // Convert px to pt approximately
      } else if (fontSizeValue.endsWith('pt')) {
        fontSize = Math.max(8, Math.min(24, parseInt(fontSizeValue.replace('pt', ''))));
      } else if (fontSizeValue.endsWith('em')) {
        const emValue = parseFloat(fontSizeValue.replace('em', ''));
        fontSize = Math.max(8, Math.min(24, defaultFontSize * emValue));
      } else if (!isNaN(parseInt(fontSizeValue))) {
        // Assume it's a pixel value if just a number
        fontSize = Math.max(8, Math.min(24, parseInt(fontSizeValue) * 0.75));
      }
    }
    
    return { text: html, color: rgbColor, fontSize: fontSize };
  }

  private stripHtmlTags(html: string): string {
    if (!html) return '';
    
    try {
      const tmpClean = document.createElement('div');
      tmpClean.innerHTML = html;
      const styleTags = tmpClean.querySelectorAll('style, script');
      styleTags.forEach(tag => tag.remove());
      
      const cleanInnerText = tmpClean.innerText || tmpClean.textContent || '';
      return cleanInnerText;
    } catch (error) {
      return '';
    }
  }
  
  private async renderHtmlToPdf(pdf: jsPDF, html: string, x: number, y: number, pageWidth: number, pageHeight: number, margin: number): Promise<number> {
    try {
      // Create a temporary container for the HTML
      const container = document.createElement('div');
      container.innerHTML = html;
      
      // Apply styling for proper rendering
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.style.width = '800px';
      container.style.backgroundColor = 'white';
      container.style.padding = '20px';
      container.style.fontFamily = 'Helvetica, Arial, sans-serif';
      container.style.fontSize = '14px';
      container.style.lineHeight = '1.4';
      
      // Add to DOM temporarily
      document.body.appendChild(container);
      
      // Use html2canvas to render the HTML as image
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: container.scrollHeight,
        logging: false
      });
      
      // Remove container from DOM
      document.body.removeChild(container);
      
      // Convert canvas to image data
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions for PDF
      const maxWidth = pageWidth - x - margin;
      const canvasAspectRatio = canvas.height / canvas.width;
      const pdfWidth = Math.min(maxWidth, 160);
      const pdfHeight = pdfWidth * canvasAspectRatio;
      
      // Check if we need a new page
      if (y + pdfHeight > pageHeight - 20) {
        pdf.addPage();
        y = margin;
      }
      
      // Add image to PDF
      pdf.addImage(imgData, 'PNG', x, y, pdfWidth, pdfHeight);
      
      // Return new Y position
      return y + pdfHeight + 10;
    } catch (error) {
      console.error('Error in renderHtmlToPdf:', error);
      
      // Fallback: show error message in PDF
      pdf.setTextColor(255, 0, 0);
      pdf.text('HTML rendering failed', x, y);
      pdf.setTextColor(0, 0, 0);
      
      return y + 10;
    }
  }
  
  private addFormattedText(pdf: jsPDF, text: string, x: number, y: number, pageWidth: number, pageHeight: number, margin: number): number {
    let currentY = y;
    const maxWidth = pageWidth - x - margin - 10;
    
    const sanitizedText = this.sanitizeTextForPdf(text);
    const paragraphs = sanitizedText.split(/\n\n+/);
    
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i];
      
      if (!paragraph.trim()) {
        continue;
      }
      
      if (currentY > pageHeight - 40) {
        pdf.addPage();
        currentY = margin;
      }
      
      // Handle different text formats
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        // Heading
        pdf.setFont('OpenSans', 'bold');
        pdf.setFontSize(14);
        
        let headingText = paragraph.slice(2, -2);
        headingText = this.sanitizeTextForPdf(headingText);
        
        const lines = this.smartTextSplit(pdf, headingText, maxWidth);
        
        for (let i = 0; i < lines.length; i++) {
          if (currentY > pageHeight - 30) {
            pdf.addPage();
            currentY = margin;
          }
          pdf.text(lines[i], x, currentY);
          currentY += 9; // Better line spacing for headings
        }
        
        pdf.setFont('OpenSans', 'normal');
        pdf.setFontSize(11);
        currentY += 6; // Extra space after heading
        
      } else if (paragraph.includes('• ') || /^\d+\. /.test(paragraph)) {
        // Handle both bullet points and numbered lists
        const listItems = paragraph.split('\n').filter(line => 
          line.trim().startsWith('• ') || /^\s*\d+\. /.test(line.trim())
        );
        
        for (const listItem of listItems) {
          if (currentY > pageHeight - 25) {
            pdf.addPage();
            currentY = margin;
          }
          
          let marker = '';
          let content = '';
          
          if (listItem.trim().startsWith('• ')) {
            marker = '• ';
            content = listItem.replace(/^\s*•\s*/, '');
          } else {
            const numberMatch = listItem.match(/^\s*(\d+\.)\s*(.*)/);
            if (numberMatch) {
              marker = numberMatch[1] + ' ';
              content = numberMatch[2];
            } else {
              marker = '• ';
              content = listItem;
            }
          }
          
          content = this.sanitizeTextForPdf(content);
          const lines = this.smartTextSplit(pdf, content, maxWidth - 20);
          
          // First line with marker
          if (lines[0]) {
            const processedLine = this.processInlineFormatting(pdf, marker + lines[0], x, currentY, maxWidth, pageHeight, margin);
            currentY = processedLine.yPosition;
          }
          
          // Subsequent lines indented
          for (let i = 1; i < lines.length; i++) {
            if (currentY > pageHeight - 20) {
              pdf.addPage();
              currentY = margin;
            }
            const processedLine = this.processInlineFormatting(pdf, '   ' + lines[i], x, currentY, maxWidth, pageHeight, margin);
            currentY = processedLine.yPosition;
          }
          currentY += 1; // Space between list items
        }
        
      } else {
        // Regular paragraph - handle inline formatting
        const sanitizedParagraph = this.sanitizeTextForPdf(paragraph);
        const processedParagraph = this.processInlineFormatting(pdf, sanitizedParagraph, x, currentY, maxWidth, pageHeight, margin);
        currentY = processedParagraph.yPosition;
      }
      
      currentY += 8;
    }
    
    return currentY;
  }
  
  private smartTextSplit(pdf: jsPDF, text: string, maxWidth: number): string[] {
    if (!text || maxWidth <= 0) return [''];
    
    // Handle Turkish characters properly by using a conservative estimate
    const conservativeMaxWidth = maxWidth * 0.95; // 5% buffer for Turkish chars
    
    try {
      const lines = pdf.splitTextToSize(text, conservativeMaxWidth);
      return Array.isArray(lines) ? lines : [String(lines)];
    } catch (error) {
      // Fallback: manual word wrapping for problematic text
      return this.manualWordWrap(text, conservativeMaxWidth);
    }
  }
  
  private manualWordWrap(text: string, maxWidth: number): string[] {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    // Rough character width estimation (conservative for Turkish)
    const avgCharWidth = 3.5;
    const maxCharsPerLine = Math.floor(maxWidth / avgCharWidth);
    
    for (const word of words) {
      const testLine = currentLine ? currentLine + ' ' + word : word;
      
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // Word is too long, break it
          if (word.length > maxCharsPerLine) {
            let remainingWord = word;
            while (remainingWord.length > maxCharsPerLine) {
              lines.push(remainingWord.substring(0, maxCharsPerLine - 1) + '-');
              remainingWord = remainingWord.substring(maxCharsPerLine - 1);
            }
            currentLine = remainingWord;
          } else {
            currentLine = word;
          }
        }
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines.length > 0 ? lines : [''];
  }
  
  private processInlineFormatting(pdf: jsPDF, text: string, x: number, startY: number, maxWidth: number, pageHeight: number, margin: number): { yPosition: number } {
    let currentY = startY;
    const lineHeight = 6;
    
    // Split text into segments with formatting
    const segments = this.parseFormattedText(text);
    
    // Process segments line by line
    let lineSegments: Array<{ text: string, bold: boolean, italic: boolean }> = [];
    let lineWidth = 0;
    
    for (const segment of segments) {
      if (!segment.text) continue;
      
      // Set font to measure width correctly
      const fontStyle = segment.bold ? 'bold' : (segment.italic ? 'italic' : 'normal');
      pdf.setFont('OpenSans', fontStyle);
      
      const segmentWidth = pdf.getTextWidth(segment.text);
      
      // Check if adding this segment would exceed line width
      if (lineWidth + segmentWidth > maxWidth && lineSegments.length > 0) {
        // Render current line
        currentY = this.renderFormattedLine(pdf, lineSegments, x, currentY, pageHeight, margin, lineHeight);
        
        // Start new line
        lineSegments = [];
        lineWidth = 0;
      }
      
      // Add segment to current line
      lineSegments.push(segment);
      lineWidth += segmentWidth;
    }
    
    // Render final line if there are segments
    if (lineSegments.length > 0) {
      currentY = this.renderFormattedLine(pdf, lineSegments, x, currentY, pageHeight, margin, lineHeight);
    }
    
    return { yPosition: currentY + 2 }; // Small gap after formatted text
  }
  
  private renderFormattedLine(
    pdf: jsPDF, 
    segments: Array<{ text: string, bold: boolean, italic: boolean }>, 
    x: number, 
    y: number, 
    pageHeight: number, 
    margin: number, 
    lineHeight: number
  ): number {
    let currentX = x;
    let currentY = y;
    
    // Check for page break
    if (currentY > pageHeight - 25) {
      pdf.addPage();
      currentY = margin;
    }
    
    for (const segment of segments) {
      if (!segment.text) continue;
      
      // Set appropriate font
      const fontStyle = segment.bold ? 'bold' : (segment.italic ? 'italic' : 'normal');
      pdf.setFont('OpenSans', fontStyle);
      
      // Render the text
      pdf.text(segment.text, currentX, currentY);
      
      // Move x position for next segment
      currentX += pdf.getTextWidth(segment.text);
    }
    
    return currentY + lineHeight;
  }
  
  private parseFormattedText(text: string): Array<{ text: string, bold: boolean, italic: boolean }> {
    const segments: Array<{ text: string, bold: boolean, italic: boolean }> = [];
    let processedText = text;
    
    // Replace our special markers with proper formatting
    processedText = processedText.replace(/@@BOLD_START@@/g, '@@B@@');
    processedText = processedText.replace(/@@BOLD_END@@/g, '@@/B@@');
    processedText = processedText.replace(/@@ITALIC_START@@/g, '@@I@@');
    processedText = processedText.replace(/@@ITALIC_END@@/g, '@@/I@@');
    
    let inBold = false;
    let inItalic = false;
    
    // Parse using our special markers
    const markerRegex = /(@@B@@|@@\/B@@|@@I@@|@@\/I@@)/g;
    let match: RegExpExecArray | null;
    let lastIndex = 0;
    
    while ((match = markerRegex.exec(processedText)) !== null) {
      // Add text before the marker
      if (match.index > lastIndex) {
        const textBefore = processedText.substring(lastIndex, match.index);
        if (textBefore) {
          segments.push({ 
            text: this.sanitizeTextForPdf(textBefore), 
            bold: inBold, 
            italic: inItalic 
          });
        }
      }
      
      // Update formatting state
      switch (match[1]) {
        case '@@B@@':
          inBold = true;
          break;
        case '@@/B@@':
          inBold = false;
          break;
        case '@@I@@':
          inItalic = true;
          break;
        case '@@/I@@':
          inItalic = false;
          break;
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < processedText.length) {
      const remainingText = processedText.substring(lastIndex);
      if (remainingText) {
        segments.push({ 
          text: this.sanitizeTextForPdf(remainingText), 
          bold: inBold, 
          italic: inItalic 
        });
      }
    }
    
    // If no segments found, add the whole text
    if (segments.length === 0) {
      segments.push({ 
        text: this.sanitizeTextForPdf(processedText), 
        bold: false, 
        italic: false 
      });
    }
    
    return segments;
  }


  private async processTableCellWidget(cellData: any, cell: any, workflowVersion: any, selectedLang: string): Promise<string> {
    if (!cellData || !cell) return 'N/A';
    
    let result = '';
    
    // First try to get direct value from cell data
    if (cellData[cell.key] !== undefined && cellData[cell.key] !== null) {
      const directValue = cellData[cell.key];
      
      if (cell.type === 'multicheckbox') {
        const selectedValues = Array.isArray(directValue) ? directValue : [];
        const optionLabels: string[] = [];
        
        if (cell.templateOptions?.options) {
          cell.templateOptions.options.forEach((option: any) => {
            if (selectedValues.includes(option.value)) {
              optionLabels.push(this.getTranslatedValue(workflowVersion, option.label, selectedLang));
            }
          });
        }
        
        result = optionLabels.length > 0 ? optionLabels.join(', ') : 'No selections';
      } else if (cell.type === 'radio') {
        if (cell.templateOptions?.options) {
          const selectedOption = cell.templateOptions.options.find((option: any) => option.value === directValue);
          result = selectedOption ? this.getTranslatedValue(workflowVersion, selectedOption.label, selectedLang) : String(directValue);
        } else {
          result = String(directValue);
        }
      } else if (cell.type === 'datepicker') {
        if (typeof directValue === 'number') {
          result = new Date(directValue).toLocaleDateString();
        } else {
          result = String(directValue);
        }
      } else if (cell.type === 'html') {
        const styleInfo = this.extractStyleFromHtml(String(directValue));
        const colorInfo = { text: styleInfo.text, color: styleInfo.color };
        const cleanedHtml = this.stripHtmlTags(colorInfo.text);
        result = this.sanitizeTextForPdf(cleanedHtml || '');
      } else if (cell.type === 'number') {
        result = String(directValue);
        if (cell.templateOptions?.unit) {
          result += ' ' + cell.templateOptions.unit;
        }
      } else {
        result = this.sanitizeTextForPdf(String(directValue));
      }
    }
    
    // Check if this cell contains nested widgets
    if (cell.items && cell.items.length > 0) {
      let widgetContent = '';
      
      for (const item of cell.items) {
        const widgetType = item.type;
        let widgetData = cellData[item.key];
        
        // Try to get data from different possible sources
        if (widgetData === undefined || widgetData === null) {
          widgetData = item.defaultValue;
        }
        
        if (widgetData === undefined || widgetData === null) {
          continue; // Skip if no data
        }
        
        switch (widgetType) {
          case 'html':
            const styleInfo = this.extractStyleFromHtml(String(widgetData));
            const cleanedHtml = this.stripHtmlTags(styleInfo.text);
            if (cleanedHtml && cleanedHtml.trim()) {
              widgetContent += this.sanitizeTextForPdf(cleanedHtml);
            }
            break;
            
          case 'image':
          case 'captureImage':
          case 'signature':
            widgetContent += '[Image]';
            break;
            
          case 'video':
          case 'captureVideo':
            widgetContent += '[Video]';
            break;
            
          case 'multicheckbox':
            if (Array.isArray(widgetData) && item.templateOptions?.options) {
              const selectedLabels = item.templateOptions.options
                .filter((opt: any) => widgetData.includes(opt.value))
                .map((opt: any) => this.getTranslatedValue(workflowVersion, opt.label, selectedLang));
              if (selectedLabels.length > 0) {
                widgetContent += selectedLabels.join(', ');
              }
            }
            break;
            
          case 'radio':
            if (item.templateOptions?.options) {
              const selectedOption = item.templateOptions.options.find((opt: any) => opt.value === widgetData);
              if (selectedOption) {
                widgetContent += this.getTranslatedValue(workflowVersion, selectedOption.label, selectedLang);
              }
            } else {
              widgetContent += String(widgetData);
            }
            break;
            
          case 'datepicker':
            if (typeof widgetData === 'number') {
              widgetContent += new Date(widgetData).toLocaleDateString();
            } else {
              widgetContent += String(widgetData);
            }
            break;
            
          case 'number':
          case 'input':
            widgetContent += String(widgetData);
            if (item.templateOptions?.unit) {
              widgetContent += ' ' + item.templateOptions.unit;
            }
            break;
            
          case 'label':
            const labelText = this.getTranslatedValue(workflowVersion, item.templateOptions?.label, selectedLang);
            if (labelText) {
              widgetContent += labelText;
            }
            break;
            
          default:
            widgetContent += this.sanitizeTextForPdf(String(widgetData));
            break;
        }
        
        // Add separator between multiple widgets in same cell
        if (widgetContent && cell.items.length > 1) {
          widgetContent += ' | ';
        }
      }
      
      // Use widget content if we have it, otherwise use the direct value
      if (widgetContent.trim()) {
        result = widgetContent;
      }
    }
    
    // Handle label cells
    if (!result && cell.type === 'label') {
      result = this.getTranslatedValue(workflowVersion, cell.templateOptions?.label, selectedLang);
    }
    
    return result || 'N/A';
  }

  private async prepareWorkflowTableData(tableData: any, tableStructure: any, workflowVersion: any, selectedLang: string): Promise<{ headers: any[], rows: any[], columnStyles: any }> {
    if (!tableData || !tableStructure) {
      return { headers: [], rows: [], columnStyles: {} };
    }

    const headers: string[] = [];
    const columnStyles: any = {};
    
    // Get headers from table structure
    if (tableStructure.columns && tableStructure.columns[0] && tableStructure.columns[0].cells) {
      tableStructure.columns[0].cells.forEach((column: any, index: number) => {
        const headerLabel = this.getTranslatedValue(workflowVersion, column.templateOptions?.label, selectedLang);
        headers.push(headerLabel);
        columnStyles[index] = { cellWidth: 'auto' };
      });
    }

    const rows: string[][] = [];
    
    // Process table rows
    if (tableStructure.rows) {
      for (const rowStructure of tableStructure.rows) {
        const row: string[] = [];
        
        for (const cell of rowStructure.cells) {
          let cellValue = '';
          
          // First check if this cell has nested widgets
          if (cell.items && cell.items.length > 0) {
            cellValue = await this.processTableCellWidget(tableData, cell, workflowVersion, selectedLang);
          } else if (tableData[cell.key] !== undefined && tableData[cell.key] !== null) {
            // Standard cell processing
            if (cell.type === 'multicheckbox') {
              // Handle checkbox values
              const selectedValues = Array.isArray(tableData[cell.key]) ? tableData[cell.key] : [];
              const optionLabels: string[] = [];
              
              if (cell.templateOptions?.options) {
                cell.templateOptions.options.forEach((option: any) => {
                  if (selectedValues.includes(option.value)) {
                    optionLabels.push(this.getTranslatedValue(workflowVersion, option.label, selectedLang));
                  }
                });
              }
              
              cellValue = optionLabels.length > 0 ? optionLabels.join(', ') : 'No selections';
            } else if (cell.type === 'radio') {
              // Handle radio values
              const selectedValue = tableData[cell.key];
              if (cell.templateOptions?.options) {
                const selectedOption = cell.templateOptions.options.find((option: any) => option.value === selectedValue);
                cellValue = selectedOption ? this.getTranslatedValue(workflowVersion, selectedOption.label, selectedLang) : String(selectedValue);
              } else {
                cellValue = String(selectedValue);
              }
            } else if (cell.type === 'datepicker') {
              // Handle date values
              const dateValue = tableData[cell.key];
              if (typeof dateValue === 'number') {
                cellValue = new Date(dateValue).toLocaleDateString();
              } else {
                cellValue = String(dateValue);
              }
            } else if (cell.type === 'number') {
              // Handle number values with units
              cellValue = String(tableData[cell.key]);
              if (cell.templateOptions?.unit) {
                cellValue += ' ' + cell.templateOptions.unit;
              }
            } else {
              // Default handling
              cellValue = String(tableData[cell.key]);
            }
          } else if (cell.type === 'label') {
            // Handle label cells
            cellValue = this.getTranslatedValue(workflowVersion, cell.templateOptions?.label, selectedLang);
          } else {
            cellValue = 'N/A';
          }
          
          row.push(cellValue);
        }
        
        rows.push(row);
      }
    }

    return { headers, rows, columnStyles };
  }

  private prepareFormData(formValues: any, formStructure: any[], workflowVersion: any, selectedLang: string): { title: string, fields: string[][] }[] {
    const formSections: { title: string, fields: string[][] }[] = [];
    
    formStructure.forEach((form: any) => {
      const section = {
        title: form.label ? this.getTranslatedValue(workflowVersion, form.label, selectedLang) : '',
        fields: [] as string[][]
      };
      
      if (form.fields) {
        form.fields.forEach((field: any) => {
          const fieldLabel = this.getTranslatedValue(workflowVersion, field.templateOptions?.label, selectedLang);
          const fieldValue = formValues[field.key] ? String(formValues[field.key]) : 'N/A';
          
          section.fields.push([fieldLabel, fieldValue]);
        });
      }
      
      formSections.push(section);
    });
    
    return formSections;
  }

  async generateAdvancedPDF(element: HTMLElement, filename: string = 'report.pdf') {
    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  private sanitizeFilename(filename: string): string {
    // Turkish character mapping
    const turkishCharMap: { [key: string]: string } = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G',
      'ı': 'i', 'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U'
    };

    // Replace Turkish characters
    let sanitized = filename;
    Object.keys(turkishCharMap).forEach(turkishChar => {
      sanitized = sanitized.replace(new RegExp(turkishChar, 'g'), turkishCharMap[turkishChar]);
    });

    // Replace non-alphanumeric characters with underscore
    sanitized = sanitized.replace(/[^a-z0-9]/gi, '_');

    // Remove multiple consecutive underscores
    sanitized = sanitized.replace(/_+/g, '_');

    // Remove leading and trailing underscores
    sanitized = sanitized.replace(/^_+|_+$/g, '');

    return sanitized || 'workflow';
  }
}
